import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Skeleton, Stack } from '@mui/material';
import { colors } from '../../theme';

function HeaderSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'flex-start' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: 1.5,
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
            <Skeleton width={170} height={22} />
            <Skeleton variant="rounded" width={56} height={20} sx={{ borderRadius: 1.5 }} />
          </Box>
          <Skeleton width={320} height={14} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        <Skeleton variant="rounded" width={240} height={36} sx={{ borderRadius: '4px' }} />
        <Skeleton variant="rounded" width={110} height={36} sx={{ borderRadius: '4px' }} />
      </Box>
    </Box>
  );
}

function StatusStripSkeleton() {
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: '100%' }}>
      {['worker', 'jobs', 'active', 'queued', 'updated'].map((slot) => (
        <Box
          key={slot}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.85,
            borderRadius: 2,
            bgcolor: '#fff',
            border: `1px solid ${colors.gray[200]}`,
            flex: '1 1 160px',
            minWidth: 0,
          }}
        >
          <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton width="60%" height={11} sx={{ mb: 0.4 }} />
            <Skeleton width="80%" height={16} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function FlowCardSkeleton() {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#fff',
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
        p: 1.5,
        pt: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 220,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: colors.gray[200],
        }}
      />

      {/* Header row */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 0.35 }}>
            <Skeleton width="60%" height={18} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" width={48} height={20} sx={{ borderRadius: 1 }} />
          </Stack>
          <Skeleton width="100%" height={13} />
          <Skeleton width="85%" height={13} />
        </Box>
      </Stack>

      {/* Stats row */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 0.25 }}>
        <Skeleton width={60} height={16} />
      </Stack>

      {/* Step pills */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {['a', 'b', 'c'].map((slot, i) => (
          <Skeleton key={slot} variant="rounded" width={90 + (i % 2) * 24} height={22} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>

      {/* Button */}
      <Box sx={{ mt: 'auto', pt: 0.75 }}>
        <Skeleton variant="rounded" width="100%" height={32} sx={{ borderRadius: 1.5 }} />
      </Box>
    </Box>
  );
}

function JobsTableSkeleton({ rows = 8 }) {
  const headSx = {
    flex: '1 1 0',
    py: 0.85,
    px: 1.5,
  };

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, borderColor: colors.gray[200], overflow: 'hidden' }}
    >
      {/* Tabs row */}
      <Box
        sx={{
          px: 1.5,
          pt: 1.25,
          pb: 0,
          borderBottom: `1px solid ${colors.gray[200]}`,
          bgcolor: colors.gray[50],
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
          <Skeleton width={130} height={18} />
          <Skeleton width={260} height={13} />
          <Box sx={{ flex: 1 }} />
          <Skeleton width={120} height={13} />
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ pb: 0.85 }}>
          {['all', 'morning', 'intraday', 'market_close', 'evening', 'recurring'].map((slot, i) => (
            <Skeleton key={slot} width={60 + (i % 3) * 12} height={20} />
          ))}
        </Stack>
      </Box>

      {/* Table header */}
      <Box
        sx={{
          display: 'flex',
          bgcolor: colors.gray[50],
          borderBottom: `1px solid ${colors.gray[200]}`,
        }}
      >
        {['Job', 'Schedule', 'Category', 'State', 'Worker', 'Action'].map((label) => (
          <Box key={label} sx={headSx}>
            <Skeleton width={label === 'Action' ? 50 : 70} height={12} />
          </Box>
        ))}
      </Box>

      {/* Table rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={`row-${i}`}
          sx={{
            display: 'flex',
            borderBottom: i === rows - 1 ? 'none' : `1px solid ${colors.gray[100]}`,
            alignItems: 'center',
          }}
        >
          {/* Job */}
          <Box sx={{ ...headSx, py: 1.1 }}>
            <Skeleton width="70%" height={14} sx={{ mb: 0.3 }} />
            <Skeleton width="55%" height={11} />
          </Box>
          {/* Schedule */}
          <Box sx={headSx}>
            <Skeleton width={100} height={14} />
          </Box>
          {/* Category */}
          <Box sx={headSx}>
            <Skeleton variant="rounded" width={84} height={22} sx={{ borderRadius: 1 }} />
          </Box>
          {/* State */}
          <Box sx={headSx}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton width={42} height={13} />
            </Stack>
          </Box>
          {/* Worker */}
          <Box sx={headSx}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton width={70} height={13} />
            </Stack>
          </Box>
          {/* Action */}
          <Box sx={{ ...headSx, display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant="rounded" width={92} height={28} sx={{ borderRadius: 1.5 }} />
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

JobsTableSkeleton.propTypes = {
  rows: PropTypes.number,
};

JobsTableSkeleton.defaultProps = {
  rows: 8,
};

function AutomationSkeleton() {
  return (
    <Box sx={{ width: '100%', px: { xs: 1.5, md: 2 }, py: 2 }}>
      <Stack spacing={1.5}>
        <HeaderSkeleton />
        <Box sx={{ height: 3 }} />
        <StatusStripSkeleton />

        {/* Flows title row */}
        <Box>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.85, px: 0.25 }}>
            <Skeleton width={120} height={16} />
            <Skeleton width={280} height={13} />
            <Box sx={{ flex: 1 }} />
            <Skeleton width={60} height={13} />
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(auto-fit, minmax(280px, 1fr))',
              },
            }}
          >
            {['flow1', 'flow2', 'flow3', 'flow4'].map((slot) => (
              <FlowCardSkeleton key={slot} />
            ))}
          </Box>
        </Box>

        <JobsTableSkeleton rows={8} />
      </Stack>
    </Box>
  );
}

export default AutomationSkeleton;
